import { useEffect, useState, useRef } from 'react';
import { useHash } from '@framework/next/navigation';
import SharedTabs from '@dealroadshow/uikit-tabs';
import TabList from './TabList';
import { ITabList } from './interfaces';

interface IProps {
  tabsList: ITabList[],
  dataTest?: string,
  autoplay?: boolean,
  initialActiveTab?: string,
}

const ANIMATION_DURATION = 7000;

const Tabs = ({
  tabsList,
  dataTest = 'featuresTabs',
  autoplay = false,
  initialActiveTab,
}: IProps) => {
  const currentHash = useHash();
  const hash = initialActiveTab || currentHash;

  const contentRef = useRef<HTMLDivElement>(null);

  const [autoPlayActive, setAutoPlayActive] = useState(autoplay);
  const [activeTab, setActiveTab] = useState<number | string>(() =>
    hash && tabsList.some((tab) => tab.key === hash) ? hash : tabsList[0].key,
  );

  const { content, key } = tabsList.find((tab) => tab.key === activeTab) ?? {};

  useEffect(() => {
    if (hash && tabsList.some((tab) => tab.key === hash)) {
      setActiveTab(hash);
    }
  }, [hash, tabsList]);
  const handleMouseOver = () => {
    setAutoPlayActive(false);
  };

  const handleMouseLeave = () => {
    setAutoPlayActive(true);
  };

  const interval = () => {
    setActiveTab((prevTabKey) => {
      const nextTab = tabsList.findIndex((tab) => tab.key === prevTabKey) + 1;
      return tabsList[nextTab]?.key ?? tabsList[0].key;
    });
  };

  useEffect(() => {
    if (contentRef.current && autoplay) {
      contentRef.current.addEventListener('mouseover', handleMouseOver);
      contentRef.current.addEventListener('mouseleave', handleMouseLeave);

      contentRef.current.addEventListener('touchstart', handleMouseOver);
      contentRef.current.addEventListener('touchend', handleMouseLeave);
    }

    return () => {
      if (contentRef.current && autoplay) {
        contentRef.current.removeEventListener('mouseover', handleMouseOver);
        contentRef.current.removeEventListener('mouseleave', handleMouseLeave);

        contentRef.current.removeEventListener('touchstart', handleMouseOver);
        contentRef.current.removeEventListener('touchend', handleMouseLeave);
      }
    };
  }, []);

  useEffect(() => {
    if (!autoPlayActive) {
      return undefined;
    }

    const tabAutoPlay = setTimeout(interval, ANIMATION_DURATION);

    return () => clearTimeout(tabAutoPlay);
  }, [autoPlayActive, activeTab]);

  return (
    <SharedTabs
      defaultTab={ activeTab }
      // @ts-ignore
      onChange={ setActiveTab }
      dataTest={ dataTest }
    >
      <div>
        <TabList tabs={ tabsList } activeTab={ activeTab } />
        <div ref={ contentRef } data-test={ `${ key }TabContent` }>{ content }</div>
      </div>
    </SharedTabs>
  );
};

export default Tabs;
